import { Outlet, Link, useLocation } from "react-router-dom";
import { Menu, X, Mail, Phone, ChevronRight, ChevronDown, ShoppingCart, User } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { publicApi } from "../lib/api";
import { useCart } from "../lib/CartContext";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";

const PublicLayout = () => {
    const [categories, setCategories] = useState([]);
    const [settings, setSettings] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [equipmentOpen, setEquipmentOpen] = useState(false);
    const equipmentRef = useRef(null);
    const { itemCount } = useCart();
    const { user } = useAuth();
    const location = useLocation();
    
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [catRes, settingsRes] = await Promise.all([
                    publicApi.getCategories(),
                    publicApi.getSettings()
                ]);
                setCategories(catRes.data);
                setSettings(settingsRes.data);
            } catch (err) {
                console.error("Failed to fetch data:", err);
            }
        };
        fetchData();
    }, []);
    
    useEffect(() => {
        setMobileMenuOpen(false);
        setEquipmentOpen(false);
    }, [location]);

    // Close equipment dropdown when clicking outside
    useEffect(() => {
        const handler = (e) => {
            if (equipmentRef.current && !equipmentRef.current.contains(e.target)) {
                setEquipmentOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const equipmentSubLinks = [
        { href: "/products", label: "New Machines" },
        { href: "/used-machines", label: "Used Machines" },
        { href: "/dry-ice-pelletizer", label: "Dry Ice Pelletizer" },
        { href: "/air-compressors", label: "Air Compressors" },
        { href: "/accessories", label: "Accessories" },
    ];

    const mainNavLinks = [
        { href: "/compare", label: "Compare" },
        { href: "/guidance", label: "Get Guidance" },
        { href: "https://secure.financ.ing/app?dealercode=VZFJY&apptype=B&qs=apply&step=1", label: "Financing", external: true },
        { href: "/rent", label: "Rent", highlight: true },
        { href: "/blog", label: "Blog" },
    ];

    const isEquipmentActive = ["/products", "/used-machines", "/dry-ice-pelletizer", "/air-compressors", "/accessories"].some(p => location.pathname.startsWith(p));
    
    return (
        <div className="min-h-screen flex flex-col bg-background">
            {/* Header */}
            <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="flex items-center justify-between h-16 lg:h-20">
                        {/* Logo */}
                        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
                            <div className="w-10 h-10 bg-primary flex items-center justify-center">
                                <span className="font-heading font-black text-primary-foreground text-xl">DI</span>
                            </div>
                            <div className="hidden sm:block">
                                <span className="font-heading font-bold text-lg tracking-tight text-foreground">DRYICE</span>
                                <span className="font-heading font-bold text-lg tracking-tight text-primary ml-1">MARKETPLACE</span>
                            </div>
                        </Link>
                        
                        {/* Desktop Navigation */}
                        <nav className="hidden lg:flex items-center gap-6">
                            {/* Equipment Dropdown */}
                            <div className="relative" ref={equipmentRef}>
                                <button
                                    onClick={() => setEquipmentOpen(!equipmentOpen)}
                                    className={`font-mono text-[11px] uppercase tracking-wider transition-colors hover:text-primary flex items-center gap-1 ${
                                        isEquipmentActive ? "text-primary" : "text-muted-foreground"
                                    }`}
                                    data-testid="nav-equipment-dropdown"
                                >
                                    Equipment
                                    <ChevronDown className={`h-3 w-3 transition-transform ${equipmentOpen ? "rotate-180" : ""}`} />
                                </button>
                                {equipmentOpen && (
                                    <div className="absolute top-full left-0 mt-3 w-56 bg-card border border-border shadow-xl z-50" data-testid="equipment-dropdown-menu">
                                        {equipmentSubLinks.map(link => (
                                            <Link
                                                key={link.href}
                                                to={link.href}
                                                className={`block px-4 py-3 font-mono text-xs uppercase tracking-wider transition-colors hover:bg-primary/10 hover:text-primary border-b border-border last:border-0 ${
                                                    location.pathname === link.href ? "text-primary bg-primary/5" : "text-muted-foreground"
                                                }`}
                                                data-testid={`nav-sub-${link.label.toLowerCase().replace(/ /g, '-')}`}
                                            >
                                                {link.label}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Other Nav Links */}
                            {mainNavLinks.map((link) => (
                                link.external ? (
                                    <a
                                        key={link.href}
                                        href={link.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-mono text-[11px] uppercase tracking-wider transition-colors hover:text-primary text-muted-foreground"
                                        data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                                    >
                                        {link.label}
                                    </a>
                                ) : (
                                    <Link
                                        key={link.href}
                                        to={link.href}
                                        className={`font-mono text-[11px] uppercase tracking-wider transition-colors hover:text-primary ${
                                            link.highlight
                                                ? "text-green-400 font-bold"
                                                : location.pathname.startsWith(link.href) ? "text-primary" : "text-muted-foreground"
                                        }`}
                                        data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                                    >
                                        {link.label}
                                    </Link>
                                )
                            ))}
                        </nav>
                        
                        {/* Desktop CTA */}
                        <div className="hidden lg:flex items-center gap-4">
                            <Link to={user ? "/account" : "/login"} data-testid="header-account-btn">
                                <Button variant="ghost" size="icon">
                                    <User className="h-5 w-5" />
                                </Button>
                            </Link>
                            <Link to="/quote">
                                <Button 
                                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider px-6"
                                    data-testid="header-request-quote-btn"
                                >
                                    Request Quote
                                </Button>
                            </Link>
                            <Link to="/cart" className="relative" data-testid="header-cart-btn">
                                <Button variant="ghost" size="icon">
                                    <ShoppingCart className="h-5 w-5" />
                                    {itemCount > 0 && (
                                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground text-xs font-bold rounded-full flex items-center justify-center">
                                            {itemCount}
                                        </span>
                                    )}
                                </Button>
                            </Link>
                        </div>
                        
                        {/* Mobile Menu */}
                        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                            <SheetTrigger asChild className="lg:hidden">
                                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                                    <Menu className="h-6 w-6" />
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="right" className="w-80 bg-card border-border">
                                <nav className="flex flex-col gap-4 mt-8">
                                    {/* Equipment section */}
                                    <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Equipment</p>
                                    {equipmentSubLinks.map(link => (
                                        <Link
                                            key={link.href}
                                            to={link.href}
                                            className={`font-mono text-base uppercase tracking-wider flex items-center justify-between pl-3 ${
                                                location.pathname === link.href ? "text-primary" : "text-foreground"
                                            }`}
                                            data-testid={`mobile-nav-${link.label.toLowerCase().replace(/ /g, '-')}`}
                                        >
                                            {link.label}
                                            <ChevronRight className="h-4 w-4" />
                                        </Link>
                                    ))}
                                    
                                    <div className="border-t border-border my-2" />
                                    
                                    {/* Other links */}
                                    {mainNavLinks.map((link) => (
                                        link.external ? (
                                            <a
                                                key={link.href}
                                                href={link.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="font-mono text-lg uppercase tracking-wider flex items-center justify-between text-foreground"
                                            >
                                                {link.label}
                                                <ChevronRight className="h-5 w-5" />
                                            </a>
                                        ) : (
                                            <Link
                                                key={link.href}
                                                to={link.href}
                                                className={`font-mono text-lg uppercase tracking-wider flex items-center justify-between ${
                                                    location.pathname.startsWith(link.href) ? "text-primary" : "text-foreground"
                                                }`}
                                            >
                                                {link.label}
                                                <ChevronRight className="h-5 w-5" />
                                            </Link>
                                        )
                                    ))}

                                    <div className="border-t border-border my-2" />
                                    
                                    <Link to={user ? "/account" : "/login"} className="font-mono text-lg uppercase tracking-wider flex items-center justify-between text-foreground">
                                        {user ? "My Account" : "Sign In"}
                                        <User className="h-5 w-5" />
                                    </Link>
                                    
                                    <Link to="/quote" className="mt-2">
                                        <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider">
                                            Request Quote
                                        </Button>
                                    </Link>
                                </nav>
                            </SheetContent>
                        </Sheet>
                    </div>
                </div>
            </header>
            
            {/* Main Content */}
            <main className="flex-1">
                <Outlet />
            </main>
            
            {/* Footer */}
            <footer className="bg-card border-t border-border mt-auto">
                <div className="container mx-auto px-6 lg:px-12 py-12 lg:py-16">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
                        {/* Brand */}
                        <div>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 bg-primary flex items-center justify-center">
                                    <span className="font-heading font-black text-primary-foreground text-xl">DI</span>
                                </div>
                                <div>
                                    <span className="font-heading font-bold text-lg text-foreground">DRYICE</span>
                                    <span className="font-heading font-bold text-lg text-primary ml-1">MARKETPLACE</span>
                                </div>
                            </div>
                            <p className="text-muted-foreground text-sm leading-relaxed">
                                {settings?.about_text || "Professional-grade dry ice blasting equipment for industrial and commercial applications. Expert guidance from selection to support."}
                            </p>
                        </div>
                        
                        {/* Quick Links */}
                        <div>
                            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-4">Quick Links</p>
                            <nav className="flex flex-col gap-2">
                                <Link to="/products" className="text-muted-foreground hover:text-primary transition-colors text-sm">All Equipment</Link>
                                <Link to="/compare" className="text-muted-foreground hover:text-primary transition-colors text-sm">Compare Systems</Link>
                                <Link to="/guidance" className="text-muted-foreground hover:text-primary transition-colors text-sm">Get Expert Guidance</Link>
                                <Link to="/used-machines" className="text-muted-foreground hover:text-primary transition-colors text-sm">Used Machines</Link>
                                <Link to="/air-compressors" className="text-muted-foreground hover:text-primary transition-colors text-sm">Air Compressors</Link>
                                <Link to="/accessories" className="text-muted-foreground hover:text-primary transition-colors text-sm">Accessories</Link>
                                <Link to="/quote" className="text-muted-foreground hover:text-primary transition-colors text-sm">Request a Quote</Link>
                                <Link to="/return-policy" className="text-muted-foreground hover:text-primary transition-colors text-sm">Return Policy</Link>
                                <Link to="/shipping-policy" className="text-muted-foreground hover:text-primary transition-colors text-sm">Shipping Policy</Link>
                                <Link to="/terms" className="text-muted-foreground hover:text-primary transition-colors text-sm">Terms & Conditions</Link>
                                <Link to="/privacy-policy" className="text-muted-foreground hover:text-primary transition-colors text-sm">Privacy Policy</Link>
                                <Link to="/about" className="text-muted-foreground hover:text-primary transition-colors text-sm">About Us</Link>
                                <Link to="/blog" className="text-muted-foreground hover:text-primary transition-colors text-sm">Blog</Link>
                            </nav>
                        </div>
                        
                        {/* Categories */}
                        <div>
                            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-4">Categories</p>
                            <nav className="flex flex-col gap-2">
                                {categories.map((cat) => (
                                    <Link
                                        key={cat.id}
                                        to={`/products?category=${cat.slug}`}
                                        className="text-muted-foreground hover:text-primary transition-colors text-sm"
                                    >
                                        {cat.name}
                                    </Link>
                                ))}
                            </nav>
                        </div>
                        
                        {/* Contact */}
                        <div>
                            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-4">Contact</p>
                            <div className="flex flex-col gap-3">
                                <a href={`mailto:${settings?.contact_email || "sales@dryicemarketplace.com"}`} className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm">
                                    <Mail className="h-4 w-4" />
                                    {settings?.contact_email || "sales@dryicemarketplace.com"}
                                </a>
                                {settings?.phone && (
                                    <a href={`tel:${settings.phone.replace(/[^0-9+]/g, '')}`} className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm">
                                        <Phone className="h-4 w-4" />
                                        {settings.phone}
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    {/* Bottom */}
                    <div className="border-t border-border mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
                        <p className="text-muted-foreground text-xs">
                            © {new Date().getFullYear()} Dryice Marketplace. All rights reserved.
                        </p>
                        <p className="text-muted-foreground/60 text-xs">
                            Powered by Dryice Marketplace · Not affiliated with any single manufacturer
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default PublicLayout;
